import {
   ChangeEvent,
   forwardRef,
   useCallback,
   useEffect,
   useImperativeHandle,
   useState,
} from 'react';

import { cloneDeep, debounce } from 'lodash';

import StoragesList from 'components/StoragesList/StoragesList';
import { PAGINATION_MAX_SIZE } from 'constants/pagination';
import { Balance, BannerVariants, StorageWithDate } from 'interfaces';
import { HTTPService } from 'service';
import { getFormattedDate } from 'utils/functions';

type ExternalStoragePageProps = {
   showBanner: (variant: BannerVariants, description: string) => void;
   openAddStorage: () => void;
   onFetchDataError: (error: unknown) => void;
};

const debounceSearchInput = debounce(async (search: string, setStorage) => {
   const { data } = await HTTPService.getStorageList({
      page: 0,
      size: PAGINATION_MAX_SIZE,
      storageType: 'EXTERNAL',
      addressSearch: search,
      sort: ['address.city,ASC', 'address.street,ASC'],
   });

   setStorage(data.content.map(content => ({ ...content, date: new Date() })));
}, 1000);

const ExternalStoragePage = forwardRef(
   ({ showBanner, openAddStorage, onFetchDataError }: ExternalStoragePageProps, ref) => {
      const [externalStorages, setExternalStorages] = useState<StorageWithDate[]>([]);
      const [loading, setLoading] = useState(true);
      const [search, setSearch] = useState('');
      const [reload, setReload] = useState<boolean>(false);

      useImperativeHandle(ref, () => ({
         reloadChild: handleForceReload,
      }));

      useEffect(() => {
         HTTPService.getStorageList({
            page: 0,
            size: PAGINATION_MAX_SIZE,
            storageType: 'EXTERNAL',
            sort: ['address.city,ASC', 'address.street,ASC'],
         })
            .then(({ data }) =>
               setExternalStorages(data.content.map(content => ({ ...content, date: new Date() }))),
            )
            .catch(onFetchDataError)
            .finally(() => setLoading(false));
      }, [onFetchDataError, reload]);

      const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
         setSearch(event.target.value);
         debounceSearchInput(event.target.value, setExternalStorages);
      };

      const handleForceReload = () => setReload(!reload);

      const replaceStorage = useCallback(
         (storageId: string, balance: Balance, date: Date) => {
            const cpExternalStorages = cloneDeep(externalStorages);
            const storageIndex = cpExternalStorages.findIndex(
               internalStorage => internalStorage.id === storageId,
            );
            cpExternalStorages[storageIndex].balanceForToday = balance;
            cpExternalStorages[storageIndex].date = date;
            setExternalStorages(cpExternalStorages);
         },
         [externalStorages],
      );

      const handleDateChange = useCallback(
         (date: Date | null, id: string, onFinish: () => void) => {
            if (date) {
               HTTPService.getStorageBalanceByDate(id, getFormattedDate(date))
                  .then(({ data }) => replaceStorage(id, data, date))
                  .catch(error => onFetchDataError(error))
                  .finally(onFinish);
            }
         },
         [replaceStorage, onFetchDataError],
      );

      return (
         <StoragesList
            loading={loading}
            storages={externalStorages}
            searchValue={search}
            handleSearchChange={handleSearchChange}
            handleDateChange={handleDateChange}
            handleForceReload={handleForceReload}
            showBanner={showBanner}
            openAddStorage={openAddStorage}
         />
      );
   },
);

export default ExternalStoragePage;
